import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import Page from '@/components/Page';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import Chip from '@/components/Chip';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format } from 'date-fns';
import { Role } from '@/routes/PrivateRoute';
import { currentAdminVar } from '@/util/apollo/cache';

// Type definitions for the API response
interface JobBasicInfo {
  id: number;
  status: string;
  published: boolean;
  filled: boolean;
  unfilled: boolean;
  notification_in_progress: boolean;
  current_notification_tier: number;
  notification_mode: string;
  first_shift_start_at: string;
  last_shift_end_at: string;
  quantity: number;
  hired_workers_count: number;
  skill: {
    id: number;
    name: string;
  };
  address: {
    id: number;
    city: string;
    state: string;
    timezone: string;
  };
}

interface NotificationStatus {
  status: string;
  reason: string;
}

interface WorkerSample {
  worker_id: number;
  worker_name: string;
  total_score: number;
  classification_reason: string;
  is_favorite: boolean;
  tier_calculated_at: string;
}

interface TierBreakdown {
  tier: number;
  count: number;
  description: string;
  sent_at: string | null;
  scheduled_at: string | null;
  workers_sample: WorkerSample[];
}

interface ScoreBreakdown {
  worker_fixed_scores_total: number;
  worker_client_scores_total: number;
  worker_skill_scores_total: number;
  worker_distance_scores: number;
  preferred_score: number;
  favorite_score: number;
}

interface BreakdownDetail {
  score: number;
  explanation: string;
}

interface FixedScoreBreakdownItem {
  type: string;
  score: number;
  explanation: string;
}

interface FixedScoresBreakdown {
  breakdown: FixedScoreBreakdownItem[];
  total_score: number;
}

interface SkillScoresBreakdown {
  explanation: string;
  shift_score: number;
  total_score: number;
  rating_count: number;
  total_shifts: number;
  ratings_score: number;
  average_rating: string;
  gravy_trained_score: number;
}

interface Breakdown {
  distance: BreakdownDetail;
  favorite: BreakdownDetail;
  preferred: BreakdownDetail;
  fixed_scores: FixedScoresBreakdown;
  skill_scores: SkillScoresBreakdown;
}

interface WorkerScore {
  worker_id: number;
  worker_name: string;
  total_score: number;
  percentile: number;
  notification_tier: number;
  tier_classification_reason: string;
  tier_calculated_at: string;
  tier_description: string;
  score_breakdown: ScoreBreakdown;
  breakdown: Breakdown;
  is_favorite: boolean;
  is_preferred: boolean;
}

interface FixedScore {
  score_type: string;
  value: number;
  client_id: number | null;
  reference_id: string | null;
  created_at: string;
}

interface FixedScoreWorker {
  worker_id: number;
  worker_name: string;
  fixed_scores: FixedScore[];
  global_scores: FixedScore[];
  client_specific_scores: FixedScore[];
}

interface SkillScore {
  worker_id: number;
  worker_name: string;
  skill_id: number;
  skill_name: string;
  shift_score: number;
  total_shifts: number;
  average_rating: number;
  rating_count: number;
  ratings_score: number;
  gravy_trained_score: number;
  total: number;
  explanation: string;
  created_at: string;
  updated_at: string;
}

interface EligibilityAnalysis {
  total_eligible_workers: number;
  tier_distribution: Record<string, number>;
  tier_percentages: Record<string, number>;
  score_ranges: {
    high_score_80_plus: number;
    mid_score_60_79: number;
    low_score_below_60: number;
  };
  favorite_workers: number;
  preferred_workers: number;
}

interface FilterStep {
  filter_name: string;
  count: number;
  description: string;
  timestamp: string;
}

interface FilterStepExplanation {
  step: number;
  name: string;
  description: string;
}

interface FilterChainSummary {
  initial_count: number;
  final_count: number;
  total_filtered_out: number;
}

interface FilterChainExplanation {
  process_description: string;
  filter_steps: FilterStepExplanation[];
  negative_criteria: string[];
}

interface EligibilityFilterChain {
  status: string;
  last_run_time?: string;
  filter_steps?: FilterStep[];
  summary?: FilterChainSummary;
  filter_chain_explanation?: FilterChainExplanation;
  message?: string;
}

interface JobDebugData {
  job_basic_info: JobBasicInfo;
  notification_status: NotificationStatus;
  tier_breakdown: TierBreakdown[];
  worker_scores: WorkerScore[];
  fixed_scores: FixedScoreWorker[];
  skill_scores: SkillScore[];
  eligibility_analysis: EligibilityAnalysis;
  eligibility_filter_chain: EligibilityFilterChain;
}

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const getStatusChip = (status: string) => {
  switch (status.toLowerCase()) {
    case 'upcoming':
      return <Chip color="warning" label="Upcoming" size="small" />;
    case 'published':
      return <Chip color="success" label="Published" size="small" />;
    case 'filled':
      return <Chip color="success" label="Filled" size="small" />;
    case 'in_progress':
      return <Chip color="warning" label="In Progress" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

interface EligibilityFilterChainProps {
  filterChain: EligibilityFilterChain;
}

const EligibilityFilterChain: React.FC<EligibilityFilterChainProps> = ({
  filterChain,
}) => {
  // Early return if required data is missing
  if (
    !filterChain.summary ||
    !filterChain.filter_steps ||
    !filterChain.filter_chain_explanation
  ) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Body color="lighter">Filter chain data is incomplete</Body>
      </div>
    );
  }

  return (
    <Stack gap={16} vertical>
      {/* Status and Summary */}
      <Stack gap={20}>
        <Stack gap={8} vertical style={{ flex: 1 }}>
          <Body weight="medium">Status:</Body>
          <Chip
            color={filterChain.status === 'available' ? 'success' : 'warning'}
            label={
              filterChain.status.charAt(0).toUpperCase() +
              filterChain.status.slice(1)
            }
            size="small"
          />
        </Stack>
        <Stack gap={8} vertical style={{ flex: 1 }}>
          <Body weight="medium">Last Run:</Body>
          <Body>
            {filterChain.last_run_time
              ? formatDate(filterChain.last_run_time)
              : 'Unknown'}
          </Body>
        </Stack>
      </Stack>

      {/* Summary Cards */}
      <div>
        <Body weight="medium">Filter Summary:</Body>
        <div style={{ marginTop: '12px' }}>
          <Stack gap={12}>
            <div
              style={{
                padding: '12px',
                backgroundColor: '#e7f3ff',
                borderRadius: '8px',
                border: '1px solid #b3d9ff',
                textAlign: 'center',
              }}
            >
              <Body weight="medium">Initial Workers</Body>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#0066cc',
                  margin: '4px 0',
                }}
              >
                {filterChain.summary.initial_count.toLocaleString()}
              </div>
            </div>
            <div
              style={{
                padding: '12px',
                backgroundColor: '#fff2e7',
                borderRadius: '8px',
                border: '1px solid #ffccb3',
                textAlign: 'center',
              }}
            >
              <Body weight="medium">Filtered Out</Body>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#cc6600',
                  margin: '4px 0',
                }}
              >
                {filterChain.summary.total_filtered_out.toLocaleString()}
              </div>
            </div>
            <div
              style={{
                padding: '12px',
                backgroundColor: '#e7f7e7',
                borderRadius: '8px',
                border: '1px solid #b3e6b3',
                textAlign: 'center',
              }}
            >
              <Body weight="medium">Final Eligible</Body>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#008000',
                  margin: '4px 0',
                }}
              >
                {filterChain.summary.final_count.toLocaleString()}
              </div>
            </div>
          </Stack>
        </div>
      </div>

      {/* Filter Steps Table */}
      <div>
        <Body weight="medium">Filter Steps:</Body>
        <div style={{ marginTop: '12px' }}>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                border: '1px solid #e1e5e9',
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e1e5e9',
                  }}
                >
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '14px',
                      width: '5%',
                    }}
                  >
                    Step
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '14px',
                      width: '25%',
                    }}
                  >
                    Filter Name
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '14px',
                      width: '40%',
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '14px',
                      width: '10%',
                    }}
                  >
                    Remaining
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '14px',
                      width: '20%',
                    }}
                  >
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody>
                {filterChain.filter_steps.map((step, index) => {
                  const isFirstStep = index === 0;
                  const previousStep =
                    index > 0 ? filterChain.filter_steps![index - 1] : null;
                  const filteredOut = previousStep
                    ? previousStep.count - step.count
                    : 0;

                  return (
                    <tr
                      key={step.filter_name}
                      style={{
                        borderBottom: '1px solid #e1e5e9',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                      }}
                    >
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#007bff',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 600,
                            margin: '0 auto',
                          }}
                        >
                          {index + 1}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>
                          {step.filter_name
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: '13px' }}>
                          {step.description}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '16px' }}>
                            {step.count.toLocaleString()}
                          </div>
                          {!isFirstStep && filteredOut > 0 && (
                            <div style={{ fontSize: '12px', color: '#dc3545' }}>
                              -{filteredOut.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px' }}>
                          {formatDate(step.timestamp)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Negative Criteria */}
      {filterChain.filter_chain_explanation.negative_criteria.length > 0 && (
        <div>
          <Body weight="medium">Common Exclusion Reasons:</Body>
          <div style={{ marginTop: '12px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '8px',
              }}
            >
              {filterChain.filter_chain_explanation.negative_criteria.map(
                (criteria, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fff2f2',
                      border: '1px solid #ffcccc',
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  >
                    • {criteria}
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      )}

      {/* Process Description */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e1e5e9',
        }}
      >
        <Body weight="medium">Process Description:</Body>
        <div style={{ marginTop: '8px', fontSize: '13px', color: '#6c757d' }}>
          {filterChain.filter_chain_explanation.process_description}
        </div>
      </div>
    </Stack>
  );
};

interface WorkerScoresTableProps {
  workers: WorkerScore[];
}

const WorkerScoresTable: React.FC<WorkerScoresTableProps> = ({ workers }) => {
  const [expandedWorker, setExpandedWorker] = useState<number | null>(null);

  const toggleExpanded = (workerId: number) => {
    setExpandedWorker(expandedWorker === workerId ? null : workerId);
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          border: '1px solid #e1e5e9',
        }}
      >
        <thead>
          <tr
            style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e1e5e9',
            }}
          >
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Worker
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Total Score
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Percentile
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Tier
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Fixed
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Client
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Skill
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Distance
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Preferred
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Favorite
            </th>
            <th
              style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Details
            </th>
          </tr>
        </thead>
        <tbody>
          {workers.map((worker, index) => (
            <React.Fragment key={worker.worker_id}>
              <tr
                style={{
                  borderBottom: '1px solid #e1e5e9',
                  backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                }}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        marginBottom: '2px',
                      }}
                    >
                      {worker.worker_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      ID: {worker.worker_id}
                    </div>
                  </div>
                </td>
                <td
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: 600,
                    color: worker.total_score >= 0 ? '#28a745' : '#dc3545',
                  }}
                >
                  {worker.total_score}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {worker.percentile}%
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor:
                        worker.notification_tier <= 2 ? '#d4edda' : '#fff3cd',
                      color:
                        worker.notification_tier <= 2 ? '#155724' : '#856404',
                    }}
                  >
                    Tier {worker.notification_tier}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {worker.score_breakdown.worker_fixed_scores_total}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {worker.score_breakdown.worker_client_scores_total}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {worker.score_breakdown.worker_skill_scores_total}
                </td>
                <td
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    color:
                      worker.score_breakdown.worker_distance_scores < 0
                        ? '#dc3545'
                        : '#28a745',
                  }}
                >
                  {worker.score_breakdown.worker_distance_scores}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {worker.score_breakdown.preferred_score}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  {worker.score_breakdown.favorite_score}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <button
                    onClick={() => toggleExpanded(worker.worker_id)}
                    style={{
                      background: 'none',
                      border: '1px solid #007bff',
                      color: '#007bff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {expandedWorker === worker.worker_id ? 'Hide' : 'Show'}
                  </button>
                </td>
              </tr>
              {expandedWorker === worker.worker_id && (
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      padding: '16px',
                      backgroundColor: '#f1f3f4',
                      borderBottom: '1px solid #e1e5e9',
                    }}
                  >
                    <div style={{ marginBottom: '12px' }}>
                      <strong
                        style={{
                          fontSize: '14px',
                          marginBottom: '8px',
                          display: 'block',
                        }}
                      >
                        Score Breakdown Details:
                      </strong>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '12px',
                      }}
                    >
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e1e5e9',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: '4px',
                            color: '#495057',
                          }}
                        >
                          Distance
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color:
                              worker.breakdown.distance.score < 0
                                ? '#dc3545'
                                : '#28a745',
                            fontWeight: 600,
                          }}
                        >
                          Score: {worker.breakdown.distance.score}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#6c757d',
                            marginTop: '4px',
                          }}
                        >
                          {worker.breakdown.distance.explanation}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e1e5e9',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: '4px',
                            color: '#495057',
                          }}
                        >
                          Favorite Status
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color:
                              worker.breakdown.favorite.score > 0
                                ? '#28a745'
                                : '#6c757d',
                            fontWeight: 600,
                          }}
                        >
                          Score: {worker.breakdown.favorite.score}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#6c757d',
                            marginTop: '4px',
                          }}
                        >
                          {worker.breakdown.favorite.explanation}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e1e5e9',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: '4px',
                            color: '#495057',
                          }}
                        >
                          Preferred Status
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color:
                              worker.breakdown.preferred.score > 0
                                ? '#28a745'
                                : '#6c757d',
                            fontWeight: 600,
                          }}
                        >
                          Score: {worker.breakdown.preferred.score}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#6c757d',
                            marginTop: '4px',
                          }}
                        >
                          {worker.breakdown.preferred.explanation}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e1e5e9',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: '4px',
                            color: '#495057',
                          }}
                        >
                          Fixed Scores
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color:
                              worker.breakdown.fixed_scores.total_score < 0
                                ? '#dc3545'
                                : worker.breakdown.fixed_scores.total_score > 0
                                  ? '#28a745'
                                  : '#6c757d',
                            fontWeight: 600,
                          }}
                        >
                          Total: {worker.breakdown.fixed_scores.total_score}
                        </div>
                        {worker.breakdown.fixed_scores.breakdown.map(
                          (item, idx) => (
                            <div
                              key={idx}
                              style={{
                                fontSize: '12px',
                                color: '#6c757d',
                                marginTop: '4px',
                                padding: '4px 0',
                                borderTop:
                                  idx === 0 ? 'none' : '1px solid #eee',
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 500,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {item.type}: {item.score > 0 ? '+' : ''}
                                {item.score}
                              </div>
                              <div>{item.explanation}</div>
                            </div>
                          ),
                        )}
                      </div>
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e1e5e9',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: '4px',
                            color: '#495057',
                          }}
                        >
                          Skill Scores
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            color:
                              worker.breakdown.skill_scores.total_score < 0
                                ? '#dc3545'
                                : '#28a745',
                            fontWeight: 600,
                          }}
                        >
                          Total: {worker.breakdown.skill_scores.total_score}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#6c757d',
                            marginTop: '4px',
                          }}
                        >
                          {worker.breakdown.skill_scores.explanation}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            marginTop: '6px',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '4px',
                          }}
                        >
                          <div>
                            Shifts: {worker.breakdown.skill_scores.total_shifts}
                          </div>
                          <div>
                            Shift Score:{' '}
                            {worker.breakdown.skill_scores.shift_score}
                          </div>
                          <div>
                            Ratings:{' '}
                            {worker.breakdown.skill_scores.rating_count}
                          </div>
                          <div>
                            Rating Score:{' '}
                            {worker.breakdown.skill_scores.ratings_score}
                          </div>
                          <div>
                            Avg Rating:{' '}
                            {worker.breakdown.skill_scores.average_rating}
                          </div>
                          <div>
                            Gravy Trained:{' '}
                            {worker.breakdown.skill_scores.gravy_trained_score}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: '12px',
                        fontSize: '12px',
                        color: '#6c757d',
                      }}
                    >
                      <strong>Classification:</strong>{' '}
                      {worker.tier_classification_reason} •
                      <strong>Calculated:</strong>{' '}
                      {formatDate(worker.tier_calculated_at)}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const JobDebugger: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const currentAdmin = useReactiveVar(currentAdminVar);
  const isTenantAdmin = currentAdmin!.role === Role.TENANT_ADMIN;

  const [loading, setLoading] = useState(true);
  const [runScoringLoading, setRunScoringLoading] = useState(false);
  const [sendNotificationLoading, setSendNotificationLoading] = useState<
    number | null
  >(null);
  const [data, setData] = useState<JobDebugData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if user is tenant admin
  if (!isTenantAdmin) {
    return (
      <Page size="full" title="Access Denied">
        <Card>
          <Card.Section>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Body>
                You do not have permission to access this page. Only tenant
                administrators can view the shift debugger.
              </Body>
            </div>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  useEffect(() => {
    if (jobId) {
      fetchJobDebugData();
    }
  }, [jobId]);

  const fetchJobDebugData = async () => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get(`/api/jobs/${jobId}/debug`);
      debugger;
      setData(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching shift debug data:', error);
      setError('Error loading shift debug data');
    } finally {
      setLoading(false);
    }
  };

  const runScoring = async () => {
    if (!jobId) return;

    setRunScoringLoading(true);
    try {
      await axiosRubyClient.post(`/api/jobs/${jobId}/run_scoring`);
      // Refresh the data after running scoring
      await fetchJobDebugData();
    } catch (error) {
      console.error('Error running scoring:', error);
      setError('Error running scoring');
    } finally {
      setRunScoringLoading(false);
    }
  };

  const sendNotification = async (tier: number) => {
    if (!jobId) return;

    setSendNotificationLoading(tier);
    try {
      await axiosRubyClient.post(`/api/jobs/${jobId}/send_notifications`, {
        tier: tier,
      });
      // Refresh the data after sending notification
      await fetchJobDebugData();
    } catch (error) {
      console.error('Error sending notification:', error);
      setError(`Error sending notification for tier ${tier}`);
    } finally {
      setSendNotificationLoading(null);
    }
  };

  if (loading) {
    return (
      <Page size="full" title="Shift Debugger">
        <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
          <LoadingState overlayColor="white" text="Loading shift debug data" />
        </Stack>
      </Page>
    );
  }

  if (error) {
    return (
      <Page size="full" title="Shift Debugger">
        <Card>
          <Card.Section>
            <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
              <Body>{error}</Body>
            </div>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page size="full" title="Shift Debugger">
        <Card>
          <Card.Section>
            <Body>No data available for Shift ID: {jobId}</Body>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      size="full"
      title={`Shift Debugger - Shift #${data.job_basic_info.id}`}
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: fetchJobDebugData,
      }}
      secondaryActions={[
        {
          a11yLabel: 'Run Analysis',
          label: runScoringLoading ? 'Running...' : 'Run Analysis',
          onAction: runScoring,
          disabled: runScoringLoading,
        },
      ]}
    >
      <Stack gap={16} vertical>
        {/* Job Basic Info */}
        <Card title="Shift Basic Information">
          <Card.Section>
            <Stack gap={16} vertical>
              <Stack gap={20}>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Shift ID:</Body>
                  <Body>{data.job_basic_info.id}</Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Status:</Body>
                  {getStatusChip(data.job_basic_info.status)}
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Skill:</Body>
                  <Body>{data.job_basic_info.skill.name}</Body>
                </Stack>
              </Stack>

              <Stack gap={20}>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Location:</Body>
                  <Body>
                    {data.job_basic_info.address.city},{' '}
                    {data.job_basic_info.address.state}
                  </Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Quantity Needed:</Body>
                  <Body>{data.job_basic_info.quantity}</Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Hired Workers:</Body>
                  <Body>{data.job_basic_info.hired_workers_count}</Body>
                </Stack>
              </Stack>

              <Stack gap={20}>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">First Shift:</Body>
                  <Body>
                    {formatDate(data.job_basic_info.first_shift_start_at)}
                  </Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Last Shift:</Body>
                  <Body>
                    {formatDate(data.job_basic_info.last_shift_end_at)}
                  </Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Current Tier:</Body>
                  <Body>{data.job_basic_info.current_notification_tier}</Body>
                </Stack>
              </Stack>

              <Stack gap={20}>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Published:</Body>
                  <Chip
                    color={data.job_basic_info.published ? 'success' : 'error'}
                    label={data.job_basic_info.published ? 'Yes' : 'No'}
                    size="small"
                  />
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Filled:</Body>
                  <Chip
                    color={data.job_basic_info.filled ? 'success' : 'warning'}
                    label={data.job_basic_info.filled ? 'Yes' : 'No'}
                    size="small"
                  />
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Notifications In Progress:</Body>
                  <Chip
                    color={
                      data.job_basic_info.notification_in_progress
                        ? 'warning'
                        : 'default'
                    }
                    label={
                      data.job_basic_info.notification_in_progress
                        ? 'Yes'
                        : 'No'
                    }
                    size="small"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Card.Section>
        </Card>

        {/* Notification Status */}
        <Card title="Notification Status">
          <Card.Section>
            <Stack gap={20}>
              <Stack gap={8} vertical style={{ flex: 1 }}>
                <Body weight="medium">Status:</Body>
                {getStatusChip(data.notification_status.status)}
              </Stack>
              <Stack gap={8} vertical style={{ flex: 2 }}>
                <Body weight="medium">Reason:</Body>
                <Body>{data.notification_status.reason}</Body>
              </Stack>
            </Stack>
          </Card.Section>
        </Card>

        {/* Tier Breakdown */}
        <Card title="Tier Breakdown">
          <Card.Section>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: 'white',
                  border: '1px solid #e1e5e9',
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #e1e5e9',
                    }}
                  >
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '14px',
                        width: '8%',
                      }}
                    >
                      Tier
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '14px',
                        width: '37%',
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                        width: '15%',
                      }}
                    >
                      Count
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                        width: '20%',
                      }}
                    >
                      Sent At
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                        width: '20%',
                      }}
                    >
                      Scheduled At
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                        width: '15%',
                      }}
                    >
                      Send Notification
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.tier_breakdown.map((tier, index) => (
                    <React.Fragment key={tier.tier}>
                      <tr
                        style={{
                          borderBottom: '1px solid #e1e5e9',
                          backgroundColor:
                            index % 2 === 0 ? 'white' : '#f8f9fa',
                        }}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor:
                                tier.tier <= 2 ? '#d4edda' : '#fff3cd',
                              color: tier.tier <= 2 ? '#155724' : '#856404',
                            }}
                          >
                            Tier {tier.tier}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontSize: '13px', color: '#495057' }}>
                            {tier.description}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '16px',
                          }}
                        >
                          {tier.count}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontSize: '13px',
                          }}
                        >
                          {tier.sent_at ? (
                            <span style={{ color: '#28a745' }}>
                              {formatDate(tier.sent_at)}
                            </span>
                          ) : (
                            <span style={{ color: '#6c757d' }}>Not sent</span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontSize: '13px',
                          }}
                        >
                          {tier.scheduled_at ? (
                            <span style={{ color: '#007bff' }}>
                              {formatDate(tier.scheduled_at)}
                            </span>
                          ) : (
                            <span style={{ color: '#6c757d' }}>
                              Not scheduled
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                          }}
                        >
                          <button
                            onClick={() => sendNotification(tier.tier)}
                            disabled={sendNotificationLoading === tier.tier}
                            style={{
                              padding: '6px 12px',
                              backgroundColor:
                                sendNotificationLoading === tier.tier
                                  ? '#6c757d'
                                  : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 500,
                              cursor:
                                sendNotificationLoading === tier.tier
                                  ? 'not-allowed'
                                  : 'pointer',
                              opacity:
                                sendNotificationLoading === tier.tier ? 0.6 : 1,
                            }}
                          >
                            {sendNotificationLoading === tier.tier
                              ? 'Sending...'
                              : 'Send'}
                          </button>
                        </td>
                      </tr>
                      {tier.workers_sample.length > 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            style={{
                              padding: '12px 16px',
                              backgroundColor: '#f1f3f4',
                              borderBottom: '1px solid #e1e5e9',
                            }}
                          >
                            <div style={{ marginBottom: '8px' }}>
                              <strong
                                style={{
                                  fontSize: '13px',
                                  color: '#495057',
                                }}
                              >
                                Sample Workers ({tier.workers_sample.length}):
                              </strong>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                              }}
                            >
                              {tier.workers_sample.map((worker) => (
                                <div
                                  key={worker.worker_id}
                                  style={{
                                    padding: '6px 10px',
                                    backgroundColor: 'white',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    border: '1px solid #dee2e6',
                                  }}
                                >
                                  <Link
                                    to={`/worker/${worker.worker_id}`}
                                    style={{
                                      textDecoration: 'none',
                                      color: '#007bff',
                                      fontWeight: '600',
                                    }}
                                    onMouseOver={(e) =>
                                      ((
                                        e.target as HTMLElement
                                      ).style.textDecoration = 'underline')
                                    }
                                    onMouseOut={(e) =>
                                      ((
                                        e.target as HTMLElement
                                      ).style.textDecoration = 'none')
                                    }
                                  >
                                    {worker.worker_name}
                                  </Link>
                                  <span style={{ color: '#6c757d' }}>
                                    {' '}
                                    (ID: {worker.worker_id})
                                  </span>
                                  <br />
                                  <span style={{ color: '#495057' }}>
                                    Score: {worker.total_score} •{' '}
                                    {worker.classification_reason}
                                  </span>
                                  {worker.is_favorite && (
                                    <span style={{ color: '#ff6b35' }}>
                                      {' '}
                                      ⭐
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Section>
        </Card>

        {/* Eligibility Analysis */}
        <Card title="Eligibility Analysis">
          <Card.Section>
            <Stack gap={16} vertical>
              <Stack gap={20}>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Total Eligible:</Body>
                  <Body>
                    {data.eligibility_analysis.total_eligible_workers}
                  </Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Favorite Workers:</Body>
                  <Body>{data.eligibility_analysis.favorite_workers}</Body>
                </Stack>
                <Stack gap={8} vertical style={{ flex: 1 }}>
                  <Body weight="medium">Preferred Workers:</Body>
                  <Body>{data.eligibility_analysis.preferred_workers}</Body>
                </Stack>
              </Stack>

              <div>
                <Body weight="medium">Score Distribution:</Body>
                <Stack gap={12}>
                  <div
                    style={{
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                    }}
                  >
                    High Score (80+):{' '}
                    {data.eligibility_analysis.score_ranges.high_score_80_plus}
                  </div>
                  <div
                    style={{
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                    }}
                  >
                    Mid Score (60-79):{' '}
                    {data.eligibility_analysis.score_ranges.mid_score_60_79}
                  </div>
                  <div
                    style={{
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                    }}
                  >
                    Low Score (&lt;60):{' '}
                    {data.eligibility_analysis.score_ranges.low_score_below_60}
                  </div>
                </Stack>
              </div>
            </Stack>
          </Card.Section>
        </Card>

        {/* Eligibility Filter Chain */}
        <Card title="Eligibility Filter Chain">
          <Card.Section>
            {data.eligibility_filter_chain &&
            data.eligibility_filter_chain.status !== 'no_logs' &&
            data.eligibility_filter_chain.summary ? (
              <EligibilityFilterChain
                filterChain={data.eligibility_filter_chain}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Body weight="medium" color="lighter">
                    {data.eligibility_filter_chain?.message ||
                      'Data not complete. Please run analysis to generate filter chain data.'}
                  </Body>
                </div>
                <button
                  onClick={runScoring}
                  disabled={runScoringLoading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: runScoringLoading ? 'not-allowed' : 'pointer',
                    opacity: runScoringLoading ? 0.6 : 1,
                  }}
                >
                  {runScoringLoading ? 'Running Analysis...' : 'Run Analysis'}
                </button>
              </div>
            )}
          </Card.Section>
        </Card>

        {/* Worker Scores */}
        {data.worker_scores.length > 0 && (
          <Card title={`Worker Scores (${data.worker_scores.length} workers)`}>
            <Card.Section>
              <WorkerScoresTable
                workers={[...data.worker_scores].sort(
                  (a, b) => b.total_score - a.total_score,
                )}
              />
            </Card.Section>
          </Card>
        )}
      </Stack>
    </Page>
  );
};

export default JobDebugger;
